import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Clock, Globe, Zap } from 'lucide-react';
import csPhoto from '../../assets/forcustomerService.jpg';

const CustomerService = () => {
  const { t } = useTranslation();

  const Card = ({ text, num, delay, anim }) => (
    <motion.div
      className="cs-card"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
    >
      <span className="cs-num">{num}</span>
      {anim}
      <p className="cs-text">{text}</p>
    </motion.div>
  );

  

  const HeadsetAnim = () => (
    <div className="cs-anim-wrap">
      <div className="cs-pulse-ring cs-ring1" />
      <div className="cs-pulse-ring cs-ring2" />
      <div className="cs-pulse-ring cs-ring3" />
      <div className="cs-anim-center">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4792f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
          <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
        </svg>
      </div>
    </div>
  );

  const RoutingAnim = () => (
    <div className="cs-route-wrap">
      <div className="cs-route-row">
        <div className="cs-route-node cs-node-main">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4792f4" strokeWidth="2.5"><circle cx="12" cy="12" r="4"/></svg>
        </div>
        <div className="cs-route-line cs-line1" />
        <div className="cs-route-node cs-node-spec cs-node-d1">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </div>
      </div>
      <div className="cs-route-row cs-route-row2">
        <div className="cs-route-line cs-line2" />
        <div className="cs-route-node cs-node-spec cs-node-d2">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </div>
        <div className="cs-route-line cs-line3" />
        <div className="cs-route-node cs-node-spec cs-node-d3">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </div>
      </div>
      <div className="cs-route-dot cs-dot1" />
      <div className="cs-route-dot cs-dot2" />
      <div className="cs-route-dot cs-dot3" />
    </div>
  );

  return (
    <section id="customer-service">
      <style>{`
        .cs-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3rem;
          box-sizing: border-box;
          overflow: hidden;
        }

        .cs-header {
          text-align: center;
          display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
        }
        .cs-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: rgba(71,146,244,0.18);
          border: 1px solid rgba(71,146,244,0.45);
          border-radius: 50px; padding: 0.35rem 1rem;
          font-size: 0.8rem; font-weight: 700; color: #a8c8ff;
          letter-spacing: 0.08em; text-transform: uppercase;
        }
        .cs-title {
          font-size: clamp(1.8rem, 3vw, 2.6rem); font-weight: 700;
          color: #ffffff; margin: 0; letter-spacing: -0.02em;
        }
        .cs-subtitle {
          font-size: 1rem; color: #c0d8ff; margin: 0;
        }

        /* Stats — decorative */
        .cs-stats {
          display: flex; gap: 0.75rem; flex-wrap: wrap; justify-content: center;
        }
        .cs-stat {
          display: flex; align-items: center; gap: 0.5rem;
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 10px; padding: 0.55rem 1rem;
        }
        .cs-stat-icon {
          width: 30px; height: 30px; border-radius: 8px;
          background: rgba(71,146,244,0.15);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .cs-stat-value {
          font-size: 1.1rem; font-weight: 800; color: #fff;
        }

        /* 3-col grid */
        .cs-grid {
          max-width: 1200px; width: 100%;
          display: grid;
          grid-template-columns: 1fr 340px 1fr;
          grid-template-rows: 1fr auto;
          gap: 1.25rem;
          align-items: stretch;
        }
        .cs-left   { grid-column: 1; grid-row: 1; display: flex; flex-direction: column; gap: 1.25rem; }
        .cs-center { grid-column: 2; grid-row: 1; display: flex; flex-direction: column; }
        .cs-right  { grid-column: 3; grid-row: 1; display: flex; flex-direction: column; gap: 1.25rem; }
        .cs-bottom { grid-column: 1 / 4; grid-row: 2; }

        /* Photo */
        .cs-photo {
          width: 100%; border-radius: 20px;
          object-fit: cover; object-position: center top;
          flex: 1; min-height: 300px; display: block;
          border: 1px solid rgba(255,255,255,0.22);
          box-shadow: 0 12px 40px rgba(0,0,0,0.35);
        }

        /* Info card */
        .cs-card {
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 14px;
          padding: 1.25rem 1.4rem;
          display: flex; flex-direction: column; gap: 0.6rem;
          flex: 1;
          transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
        }
        .cs-card:hover {
          background: rgba(255,255,255,0.11);
          border-color: rgba(71,146,244,0.45);
          box-shadow: 0 4px 20px rgba(71,146,244,0.13);
        }
        .cs-num {
          font-size: 1.4rem; font-weight: 800;
          color: rgba(71,146,244,0.45); line-height: 1;
        }
        .cs-text {
          font-size: 0.9rem; color: #d0e4ff; line-height: 1.75; margin: 0;
        }

        /* Tagline CTA */
        .cs-cta {
          background: linear-gradient(135deg, #4792f4 0%, #2563eb 100%);
          border-radius: 14px;
          padding: 1.5rem 1.75rem;
          box-shadow: 0 8px 24px rgba(71,146,244,0.4);
          display: flex; align-items: center; gap: 1rem;
        }
        .cs-cta-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .cs-cta-text {
          font-size: 1rem; font-weight: 700;
          color: #ffffff; margin: 0; line-height: 1.5;
        }

        /* ── Headset pulse animation (Card 01) ── */
        .cs-anim-wrap {
          position: relative;
          width: 90px; height: 90px;
          display: flex; align-items: center; justify-content: center;
          margin: 0.5rem auto 0.25rem;
        }
        .cs-pulse-ring {
          position: absolute; border-radius: 50%;
          border: 2px solid rgba(71,146,244,0.5);
          animation: csPulse 2.4s ease-out infinite;
        }
        .cs-ring1 { width: 90px; height: 90px; animation-delay: 0s; }
        .cs-ring2 { width: 68px; height: 68px; animation-delay: 0.5s; border-color: rgba(71,146,244,0.4); }
        .cs-ring3 { width: 48px; height: 48px; animation-delay: 1s; border-color: rgba(71,146,244,0.3); }
        @keyframes csPulse {
          0%   { transform: scale(0.85); opacity: 0.9; }
          70%  { transform: scale(1.15); opacity: 0; }
          100% { transform: scale(1.15); opacity: 0; }
        }
        .cs-anim-center {
          width: 44px; height: 44px; border-radius: 50%;
          background: rgba(71,146,244,0.15);
          border: 1.5px solid rgba(71,146,244,0.4);
          display: flex; align-items: center; justify-content: center;
          position: relative; z-index: 1;
          animation: csGlow 2.4s ease-in-out infinite;
        }
        @keyframes csGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(71,146,244,0); }
          50%       { box-shadow: 0 0 14px 4px rgba(71,146,244,0.3); }
        }

        /* ── Routing animation (Card 02) ── */
        .cs-route-wrap {
          position: relative;
          width: 100%; height: 80px;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 6px; margin: 0.5rem 0 0.25rem;
        }
        .cs-route-row {
          display: flex; align-items: center; gap: 6px;
        }
        .cs-route-row2 {
          justify-content: center;
        }
        .cs-route-node {
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .cs-node-main {
          width: 40px; height: 40px;
          background: rgba(71,146,244,0.18);
          border: 2px solid rgba(71,146,244,0.5);
          animation: csNodePulse 2s ease-in-out infinite;
        }
        @keyframes csNodePulse {
          0%, 100% { border-color: rgba(71,146,244,0.5); background: rgba(71,146,244,0.18); }
          50%       { border-color: rgba(71,146,244,0.9); background: rgba(71,146,244,0.32); }
        }
        .cs-node-spec {
          width: 30px; height: 30px;
          border: 1.5px solid;
          background: rgba(255,255,255,0.06);
        }
        .cs-node-d1 { border-color: rgba(16,185,129,0.6); animation: csNodeSpec1 2s ease-in-out 0.3s infinite; }
        .cs-node-d2 { border-color: rgba(245,158,11,0.6); animation: csNodeSpec2 2s ease-in-out 0.6s infinite; }
        .cs-node-d3 { border-color: rgba(167,139,250,0.6); animation: csNodeSpec3 2s ease-in-out 0.9s infinite; }
        @keyframes csNodeSpec1 {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.12); }
        }
        @keyframes csNodeSpec2 {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.12); }
        }
        @keyframes csNodeSpec3 {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.12); }
        }
        .cs-route-line {
          height: 2px; background: linear-gradient(90deg, rgba(71,146,244,0.6), rgba(71,146,244,0.2));
          border-radius: 2px; flex-shrink: 0;
        }
        .cs-line1 { width: 30px; }
        .cs-line2 { width: 22px; }
        .cs-line3 { width: 22px; }

        /* travelling dot on lines */
        .cs-route-dot {
          position: absolute;
          width: 6px; height: 6px; border-radius: 50%;
          background: #4792f4;
        }
        .cs-dot1 { animation: csDot1 2s linear infinite; top: 8px; }
        .cs-dot2 { animation: csDot2 2s linear 0.5s infinite; top: 50px; }
        .cs-dot3 { animation: csDot3 2s linear 1s infinite; top: 50px; }
        @keyframes csDot1 {
          0%   { left: 28%; opacity: 1; }
          50%  { left: 50%; opacity: 1; }
          100% { left: 65%; opacity: 0; }
        }
        @keyframes csDot2 {
          0%   { left: 28%; opacity: 0; }
          20%  { opacity: 1; }
          100% { left: 12%; opacity: 0; }
        }
        @keyframes csDot3 {
          0%   { left: 50%; opacity: 0; }
          20%  { opacity: 1; }
          100% { left: 72%; opacity: 0; }
        }

        /* Mobile */
        @media (max-width: 860px) {
          .cs-grid {
            grid-template-columns: 1fr;
            grid-template-rows: auto;
          }
          .cs-center { grid-column: 1; grid-row: 1; }
          .cs-left   { grid-column: 1; grid-row: 2; }
          .cs-right  { grid-column: 1; grid-row: 3; }
          .cs-bottom { grid-column: 1; grid-row: 4; }
          .cs-photo  { height: 240px; min-height: unset; }
          .cs-cta    { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="cs-section">
        <motion.div
          className="cs-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="cs-badge">
            <MessageCircle size={13} />
            Customer Service
          </div>
          <h2 className="cs-title">Customer Service</h2>
          <p className="cs-subtitle">{t('customerServiceSubtitle')}</p>

          <div className="cs-stats">
            {[
              { icon: <Clock size={15} color="#4792f4" />, val: '24/7' },
              { icon: <Globe size={15} color="#10b981" />, val: '7+' },
              { icon: <Zap size={15} color="#f59e0b" />, val: '10–20 min' },
            ].map((s, i) => (
              <motion.div
                className="cs-stat" key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              >
                <div className="cs-stat-icon">{s.icon}</div>
                <div className="cs-stat-value">{s.val}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="cs-grid">
          {/* Left */}
          <div className="cs-left">
            <Card text={t('customerServiceP1')} num="01" delay={0.1} anim={<HeadsetAnim />} />
          </div>

          {/* Center — photo */}
          <motion.div
            className="cs-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img className="cs-photo" src={csPhoto} alt="Customer Service" />
          </motion.div>

          {/* Right */}
          <div className="cs-right">
            <Card text={t('customerServiceP2')} num="02" delay={0.1} anim={<RoutingAnim />} />
          </div>

          {/* Bottom — tagline */}
          <motion.div
            className="cs-bottom"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <div className="cs-cta">
              <div className="cs-cta-icon">
                <MessageCircle size={22} color="#fff" />
              </div>
              <p className="cs-cta-text">{t('customerServiceTagline')}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CustomerService;
